import * as yup from "yup";
import "yup-phone-lite";
import { CountryCode } from "libphonenumber-js";
import {
  countriesCollection,
  crmIDFinalValueRegex,
  nameRegex,
} from "./form-constants";
import "./yup-extensions";

const validCountryValues = countriesCollection.map(({ value }) => value);
const countryFieldValidation = yup
  .string()
  .trim()
  .required("Select country")
  .oneOf(validCountryValues, "Select country");

export const schemaConstructor = (
  schemaType: string,
  options: {
    locale?: CountryCode;
    presValue?: string;
    isInternalForm?: boolean;
    isAuthenticatedForm?: boolean;
    isGPForm?: boolean;
    isDevelopmentForm?: boolean;
    isMemorialGivingForm?: boolean;
    isKckForm?: boolean;
  } = {},
) => {
  if (schemaType === "donationType") {
    const sharedTributeFields = {
      recipientEmail: yup.string().when("isTributeGift", {
        is: true,
        then: yup.string().when("honourCardType", {
          is: "ecard",
          then: yup
            .string()
            .trim()
            .emailWithValidTLD("Provide valid email")
            .required("Enter recipient email"),
        }),
      }),
      emailSubject: yup
        .string()
        .trim()
        .when("isTributeGift", {
          is: true,
          then: yup.string().when("honourCardType", {
            is: "ecard",
            then: yup
              .string()
              .min(4, "Subject greater than 4")
              .required(
                options.isMemorialGivingForm
                  ? "Provide email subject"
                  : "Provide ecard subject",
              ),
          }),
        }),
    };

    if (options.isMemorialGivingForm) {
      return {
        donationType: yup.string().required("Select gift type"),
        donationAmount: yup.number().moreThan(4.99, "More than 5"),
        honourType: yup.string().when("isTributeGift", {
          is: true,
          then: yup.string().required("Select honour gift type"),
        }),
        honoureeFirstName: yup
          .string()
          .trim()
          .when("isTributeGift", {
            is: true,
            then: yup.string().required("Enter honouree first name"),
          }),
        honoureeLastName: yup
          .string()
          .trim()
          .when("isTributeGift", {
            is: true,
            then: yup.string().required("Enter honouree last name"),
          }),
        recipientFirstName: yup
          .string()
          .trim()
          .when("isTributeGift", {
            is: true,
            then: yup.string().when("honourCardType", {
              is: (val: string) => val === "ecard",
              then: yup.string().required("Enter recipient first name"),
            }),
          }),
        recipientLastName: yup
          .string()
          .trim()
          .when("isTributeGift", {
            is: true,
            then: yup.string().when("honourCardType", {
              is: (val: string) => val === "ecard",
              then: yup.string().required("Enter recipient last name"),
            }),
          }),
        ...sharedTributeFields,
        cardActive: yup.number().when("isTributeGift", {
          is: true,
          then: yup.number().when("honourCardType", {
            is: (val: string) => val === "ecard",
            then: yup.number().moreThan(0, "Select card"),
          }),
        }),
        loyaltyCardNumber: yup
          .string()
          .test("length-when-provided", "Loyalty card equal 9", (value) => {
            if (!value) return true;
            return value.length === 9;
          }),
        loyaltyCardNumberRepeat: yup.string().when("loyaltyCardNumber", {
          is: (val: string) => Boolean(val),
          then: (schema) =>
            schema.oneOf(
              [yup.ref("loyaltyCardNumber")],
              "Loyalty card numbers do not match",
            ),
          otherwise: (schema) => schema,
        }),
      };
    }
    return {
      donationType: yup.string().required("Select gift type"),
      donationAmount: yup.number().moreThan(4.99, "More than 5"),
      honourType: yup.string().when("isTributeGift", {
        is: true,
        then: yup.string().required("Select honour gift type"),
      }),
      honoureeFirstName: yup
        .string()
        .trim()
        .when("isTributeGift", {
          is: true,
          then: yup.string().required("Enter honouree first name"),
        }),
      honoureeLastName: yup
        .string()
        .trim()
        .when("isTributeGift", {
          is: true,
          then: yup.string().required("Enter honouree last name"),
        }),
      recipientFirstName: yup
        .string()
        .trim()
        .when("isTributeGift", {
          is: true,
          then: yup.string().when("honourCardType", {
            is: (val: string) => val !== "nocard",
            then: yup.string().required("Enter recipient first name"),
          }),
        }),
      recipientLastName: yup
        .string()
        .trim()
        .when("isTributeGift", {
          is: true,
          then: yup.string().when("honourCardType", {
            is: (val: string) => val !== "nocard",
            then: yup.string().required("Enter recipient last name"),
          }),
        }),
      ...sharedTributeFields,
      honoureeAddressLine: yup.string().when("isTributeGift", {
        is: true,
        then: yup.string().when("honourCardType", {
          is: "mail",
          then: yup.string().trim().required("Enter street address"),
        }),
      }),
      honoureeCity: yup.string().when("isTributeGift", {
        is: true,
        then: yup.string().when("honourCardType", {
          is: "mail",
          then: yup.string().trim().required("Enter city"),
        }),
      }),
      honoureePostal: yup.string().when("isTributeGift", {
        is: true,
        then: yup.string().when("honourCardType", {
          is: "mail",
          then: yup.string().trim().required("Enter postal"),
        }),
      }),
      honoureeProvinceState: yup.string().when("isTributeGift", {
        is: true,
        then: yup.string().when("honourCardType", {
          is: "mail",
          then: yup.string().trim().required("Select State / Province"),
        }),
      }),
      honoureeCountry: yup.string().when("isTributeGift", {
        is: true,
        then: yup.string().when("honourCardType", {
          is: "mail",
          then: countryFieldValidation,
        }),
      }),
      cardActive: yup.number().when("isTributeGift", {
        is: true,
        then: yup.number().when("honourCardType", {
          is: (val: string) => val === "mail" || val === "ecard",
          then: yup.number().moreThan(0, "Select card"),
        }),
      }),
      isTributeGift: yup.boolean(),
      honourCardType: yup.string(),
      loyaltyCardNumber: yup
        .string()
        .test("length-when-provided", "Loyalty card equal 9", (value) => {
          if (!value) return true;
          return value.length === 9;
        }),
      loyaltyCardNumberRepeat: yup.string().when("loyaltyCardNumber", {
        is: (val: string) => Boolean(val),
        then: (schema) =>
          schema.oneOf(
            [yup.ref("loyaltyCardNumber")],
            "Loyalty card numbers do not match",
          ),
        otherwise: (schema) => schema,
      }),
    };
  } else if (schemaType === "donorInfo") {
    return {
      ...(options.isAuthenticatedForm || options.isInternalForm
        ? {
            companyName: yup.string().when("isCompanyGift", {
              is: true,
              then: yup
                .string()
                .trim()
                .matches(/[^0-9 ]/, {
                  message: "Invalid company name number only",
                  excludeEmptyString: true,
                })
                .required("Enter company name"),
            }),
          }
        : {}),
      ...(!options.isAuthenticatedForm &&
      !options.isInternalForm &&
      !options.isKckForm
        ? {
            email: yup
              .string()
              .trim()
              .min(5, "Please provide email that is more than 5 characters")
              .max(150)
              .required(
                options.isGPForm
                  ? "Provide valid email or select checkbox"
                  : "Enter email",
              )
              .emailWithValidTLD("Provide valid email"),
          }
        : {}),
      firstName: yup
        .string()
        .trim()
        .required("Enter first name")
        .matches(nameRegex, "Enter first name")
        .min(1, "First name greater than 1")
        .max(50, "First name less than 50"),
      lastName: yup
        .string()
        .trim()
        .required("Enter last name")
        .matches(nameRegex, "Enter last name")
        .min(1, "Last name greater than 1")
        .max(100, "Last name less than 50"),
      ...(!options.isAuthenticatedForm && !options.isInternalForm
        ? {
            companyName: yup.string().when("isCompanyGift", {
              is: true,
              then: yup
                .string()
                .trim()
                .matches(/[^0-9]/, {
                  message: "Invalid company name number only",
                  excludeEmptyString: true,
                })
                .required("Enter company name"),
            }),
          }
        : {}),
      addressLine1: yup.string().trim().required("Enter street address"),
      city: yup.string().trim().required("Enter city"),
      provinceState: yup.string().trim().required("Select State / Province"),
      postal: yup.string().trim().required("Enter postal"),
      country: countryFieldValidation,
      phoneNumber: yup.string().test({
        name: "phone",
        test: function (value: string | undefined) {
          if (value !== "") {
            return (
              yup
                .string()
                .phone(options.locale)
                // .phone(["US", "CA", "GB", "AE", "FR"])
                .isValidSync(value)
            );
          }
          return true; // Skip validation if value is empty
        },
        message: "Invalid phone number",
      }),
      ...(options.isAuthenticatedForm || options.isInternalForm
        ? {
            email: yup
              .string()
              .trim()
              .min(5, "Please provide email that is more than 5 characters")
              .max(150)
              .required(
                options.isInternalForm
                  ? "Provide valid email or select checkbox"
                  : "Enter email",
              )
              .emailWithValidTLD("Provide valid email"),
          }
        : {}),
      callCentreNumber:
        options.presValue === "CC" || options.isGPForm
          ? yup
              .string()
              .trim()
              .required(
                options.isGPForm
                  ? "Enter Appeal Code"
                  : "Enter Call Centre Call Number",
              )
          : options.isDevelopmentForm &&
            yup
              .string()
              .trim()
              .test({
                name: "callCentreNumber",
                test: function (value: string | undefined) {
                  if (value && !crmIDFinalValueRegex.test(value)) {
                    return false;
                  }
                  return true; // Skip validation if value is empty
                },
                message: "Invalid CRM ID",
              }),
      callCentreAgentName:
        options.presValue === "CC" || options.isKckForm
          ? yup
              .string()
              .trim()
              .required(
                options.isKckForm
                  ? "Enter agent name"
                  : "Enter Call Centre Agent Name",
              )
          : yup.string(),
      ...(options.isKckForm && {
        appealCodeOverride: yup.string().trim().required("Select appeal code"),
        appealPackage: yup.string().when("appealCodeOverride", {
          is: (value: string) => {
            console.log("value", value);
            return !value?.includes("MBKZDM") && !value?.includes("WMZZWM");
          },
          then: yup.string().trim().required("Enter appeal package"),
          otherwise: yup.string().trim(),
        }),
      }),
      isCompanyGift: yup.boolean(),
    };
  } else if (schemaType === "debit") {
    return {
      ...(options.isKckForm && {
        processingDate: yup.string().trim().required("Select processing date"),
      }),
      institutionNumber: yup.string().min(3, "Institution number 3 digits"),
      transitNumber: yup.string().min(5, "Transit number 5 digits"),
      accountNumber: yup.string().min(5, "Account number 5 digits"),
      verifyAccountNumber: yup
        .string()
        .min(5, "Verified account number 5 digits")
        .test(
          "accounts-match",
          "Account numbers do not match",
          function (value) {
            return value === this.resolve(yup.ref("accountNumber"));
          },
        ),
    };
  }
};

export const overlayDonorInfoSchema = {
  email: yup
    .string()
    .trim()
    .min(5, "Please provide email that is more than 5 characters")
    .max(150)
    .required("Enter email")
    .emailWithValidTLD("Provide valid email"),
  firstName: yup
    .string()
    .trim()
    .required("Enter first name")
    .matches(nameRegex, "Enter first name")
    .min(1, "First name greater than 1")
    .max(50, "First name less than 50"),
  lastName: yup
    .string()
    .trim()
    .required("Enter last name")
    .matches(nameRegex, "Enter last name")
    .min(1, "Last name greater than 1")
    .max(100, "Last name less than 50"),
  phoneNumber: yup.string().test({
    name: "phone",
    test: function (value: string | undefined) {
      if (value !== "") {
        return yup.string().phone("CA").isValidSync(value);
      }
      return true; // Skip validation if value is empty
    },
    message: "Invalid phone number",
  }),
};

export const overlayAddressInfoSchema = {
  addressLine1: yup.string().trim().required("Enter street address"),
  city: yup.string().trim().required("Enter city"),
  provinceState: yup.string().trim().required("Select State / Province"),
  postal: yup.string().trim().required("Enter postal"),
  country: countryFieldValidation,
};

export const gosDonorInfoSchema = {
  title: yup.string().trim().required("Select title"),
  firstName: yup
    .string()
    .trim()
    .required("Enter first name")
    .matches(nameRegex, "Enter first name")
    .min(1, "First name greater than 1")
    .max(50, "First name less than 50"),
  lastName: yup
    .string()
    .trim()
    .required("Enter last name")
    .matches(nameRegex, "Enter last name")
    .min(1, "Last name greater than 1")
    .max(100, "Last name less than 50"),
  addressLine1: yup.string().trim().required("Enter street address"),
  city: yup.string().trim().required("Enter city"),
  provinceState: yup.string().trim().required("Select State / Province"),
  postal: yup.string().trim().required("Enter postal"),
  country: countryFieldValidation,
  phoneNumber: yup.string().test({
    name: "phone",
    test: function (value: string | undefined) {
      if (!value) return false;
      return yup.string().phone("CA").isValidSync(value);
    },
    message: "Invalid phone number",
  }),
  email: yup
    .string()
    .trim()
    .min(5, "Please provide email that is more than 5 characters")
    .max(150)
    .required("Enter email")
    .emailWithValidTLD("Provide valid email"),
  securitiesManagementType: yup.string().required("Select management type"),
};

export const gosSelfManagedInstitutionSchema = {
  selfDirectedPlatform: yup.string().required("Select platform"),
  securitiesAccountNumber: yup.string().required("Enter account number"),
};

export const gosBrokerManagedInstitutionSchema = {
  brokerageName: yup.string().required("Select brokerage"),
  securitiesAccountNumber: yup.string().required("Enter account number"),
  advisorFirstName: yup.string().required("Enter advisor first name"),
  advisorLastName: yup.string().required("Enter advisor last name"),
  advisorPhoneNumber: yup.string().test({
    name: "phone",
    test: function (value: string | undefined) {
      if (value !== "") {
        return yup.string().phone("CA").isValidSync(value);
      }
      return true; // Skip validation if value is empty
    },
    message: "Invalid phone number",
  }),
  advisorEmail: yup
    .string()
    .trim()
    .min(5, "Please provide email that is more than 5 characters")
    .max(150)
    .required("Enter email")
    .emailWithValidTLD("Provide valid email"),
};

export const gosSecurityItemSchema = {
  securityName: yup.string().trim().required("Enter security name"),
  securityType: yup.string(),
  cusipOrSymbol: yup.string(),
  numberOfUnits: yup
    .string()
    .trim()
    .when("approximateDollarValue", {
      is: (val: string) => !val || val.trim() === "",
      then: (schema) =>
        schema.required(
          "Enter number of units / shares or approximate dollar value",
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
  approximateDollarValue: yup
    .string()
    .trim()
    .when("numberOfUnits", {
      is: (val: string) => !val || val.trim() === "",
      then: (schema) =>
        schema.required(
          "Enter approximate dollar value or number of units / shares",
        ),
      otherwise: (schema) => schema.notRequired(),
    }),
};
